import { CONSTANTS } from "client/utils/constants";
import { getOnlyNumbers, initializeReason, getNumberOrNull } from "client/utils/RequisitionUtil";
import { IOrder, IOrderReason, IOrderRequest } from "Model/IApiObjectRequestData";
import { IProductionVehicle } from "Model/IProductionVehicle";
import { IReason } from "Model/IReason";
import { IRequest } from "Model/IRequest";
import { IAttachment } from "Model/IAttachment";
import { formatPrice } from 'utils'
import UploadAttachmentsDispatch from "Store/AssetManagement/UploadAttachments/UploadAttachmentsDispatch";
import RequisitionAction from "Store/Component/Requisition/RequisitionAction";
import RequisitionValidation from "../../client/Components/OrderManagement/Requisition/RequisitionValidation"
import { IManagedEquipment } from "Model/IManagedEquipment";
import { INonStandardEquipment } from "Model/INonStandardEquipment";
import { IManagementVehicle, IVehicleDriverDetails } from "Model/IManagementVehicle";
import moment from "moment";

const parseIntOrDefault = (value: string, defaultValue: number = 0): number => {
  return value !== "" ? parseInt(value) : defaultValue;
};

const parseFloatOrDefault = (value: string, defaultValue: number = 0.0): number => {
  return value !== "" ? parseFloat(getOnlyNumbers(value)) : defaultValue;
};

const parseFloatOrNull = (value: string): number | null => {
  return value ? parseFloat(getOnlyNumbers(value)) : null;
};

const getEmptyDealerInfo = () => ({
  orderRequestManagedEquipmentDealerId: 0,
  orderRequestDetailId: 0,
  dealerName: "",
  quotePrice: 0.00,
  estimatedPrice: 0.00,
  dealerPhone: "",
  dealerContact: "",
  dealerEmail: "",
  dealerAdd1: "",
  dealerAdd2: "",
  dealerAdd3: "",
  dealerCity: "",
  dealerState: "",
  dealerZip: ""
});

const createDealerInfo = (reasonCartItem: any) => ({
  orderRequestManagedEquipmentDealerId: 0,
  orderRequestDetailId: 0,
  dealerName: reasonCartItem?.name || "",
  quotePrice: parseFloatOrDefault(reasonCartItem?.quotePrice, 0.00),
  estimatedPrice: parseFloatOrDefault(reasonCartItem?.estimatedPrice, 0.00),
  dealerPhone: reasonCartItem?.phone || "",
  dealerContact: reasonCartItem?.contact || "",
  dealerEmail: reasonCartItem?.email || "",
  dealerAdd1: reasonCartItem?.addressLine1 || "",
  dealerAdd2: reasonCartItem?.addressLine2 || "",
  dealerAdd3: reasonCartItem?.addressLine3 || "",
  dealerCity: reasonCartItem?.city || "",
  dealerState: reasonCartItem?.state || "",
  dealerZip: reasonCartItem?.zipCode || ""
});

const createOrderReason = (reasonCartItem: any, includeDealer: boolean): IOrderReason => {
  const hasDisposalDate = !!reasonCartItem?.replacementAssetDisposalDate;
  return {
    reasonId: reasonCartItem?.reasonId,
    dispositionTypeId: hasDisposalDate ? 0 : parseIntOrDefault(reasonCartItem?.statusId),
    conditionTypeId: hasDisposalDate ? 0 : parseIntOrDefault(reasonCartItem?.conditionId),
    assetNumber: parseIntOrDefault(reasonCartItem?.asset),
    equipmentNumber: reasonCartItem?.unitNumber,
    description: reasonCartItem?.description,
    mileageHourMeter: hasDisposalDate ? 0.0 : parseFloatOrDefault(reasonCartItem?.mileageHourMeter, 0.0),
    year: parseIntOrDefault(reasonCartItem?.modelYear),
    netBookedValue: reasonCartItem?.netBookedValue !== "" ? getNumberOrNull(reasonCartItem?.netBookedValue) : null,
    approximateDisposeValue: hasDisposalDate ? null : parseFloatOrNull(reasonCartItem?.anticipatedDisposalPrice),
    replacementAssetDisposalDate: reasonCartItem?.replacementAssetDisposalDate || null,
    reason: reasonCartItem?.reason,
    reasonTypeId: parseIntOrDefault(reasonCartItem?.reasonTypeId),
    isNewEmployee: reasonCartItem?.newEmployeeBusiness === CONSTANTS.NEW_EMPLOYEE,
    jobOrEmpId: reasonCartItem?.jobIdOrEmpId,
    jobOrEmpName: reasonCartItem?.jobOrEmpName,
    salesAmount: parseFloatOrDefault(reasonCartItem?.salesAmount, 0.00),
    comments: reasonCartItem?.comments,
    shipToLocation: reasonCartItem?.shipToLocation,
    reasonAttachments: reasonCartItem?.attachments,
    hasQuoteAttached: reasonCartItem?.hasQuoteAttached,
    managedEquipmentDealer: includeDealer ? createDealerInfo(reasonCartItem) : getEmptyDealerInfo(),
    showNewEmployee: !reasonCartItem?.jobIdOrEmpId
  };
};

const attachReasonsToRequest = (orderRequest: IOrderRequest, requestIndex: number, reasonCartList: any[], includeDealer: boolean): void => {
  for (let j = 0; j < reasonCartList?.length; j++) {
    if (requestIndex === reasonCartList[j].requestIndex) {
      const reason = createOrderReason(reasonCartList[j], includeDealer);
      orderRequest.reasons.push(reason);
    }
  }
};

export const ReactToAPI = (props: any) => {

  const orderId = props.orderId;
  const productionVehicleCartList = props.productionVehicleCartList;
  const nonStandardEquipmentCartList = props.nonStandardEquipmentCartList;
  const managementVehicleCartList = props.managementVehicleCartList;
  const reasonCartList = props.reasonCartList;
  const selectedBranch = props.selectedBranch;
  const getUserDetails = props.getUserDetails;
  const orderAction = props.orderAction;
  const dispatch = props.dispatch;
  const attachmentList = props.attachmentList;
  const managedEquipmentCartList = props?.managedEquipmentCartList;
  const isMandAFlag = props?.isMandAFlag
  const isApprovalOverrideFlag = props?.isApprovalOverrideFlag;
  const isDealerSame = props?.isDealerSameForAllFlag;

  const apiObject: IOrder = {
    orderId: 0,
    branchId: 0,
    userEmailId: "",
    savedBy: "",
    orderAction: "",
    attachments: attachmentList,
    requests: [],
    IsMandAFlag: false,
    isApprovalOverride: false,
    isDealerSame : false
  }

  apiObject.orderId = orderId;
  apiObject.branchId = selectedBranch?.text;
  
  apiObject.userEmailId = getUserDetails?.emailId;
  apiObject.savedBy = getUserDetails?.emailId;
  apiObject.orderAction = orderAction;
  apiObject.IsMandAFlag = !!isMandAFlag;
  apiObject.isApprovalOverride = isApprovalOverrideFlag;
  apiObject.isDealerSame = isDealerSame;

  let productionVehicleListTemp = productionVehicleCartList?.length > 0 ? [...productionVehicleCartList] : [];
  for (let i = 0; i < productionVehicleListTemp?.length; i++) {
    let orderRequest: IOrderRequest = {
      requestId: productionVehicleListTemp[i]?.requestId,
      orderTypeId: (productionVehicleListTemp[i]?.orderTypeId !== "") ? parseInt(productionVehicleListTemp[i]?.orderTypeId) : 0,
      orderRequestSubTypeId: (productionVehicleListTemp[i]?.orderSubTypeId !== "") ? parseInt(productionVehicleListTemp[i]?.orderSubTypeId) : 0,
      vehicleTypeId: productionVehicleListTemp[i]?.vehicleTypeId,
      vehicleCapacityId: productionVehicleListTemp[i]?.vehicleCapacityId,
      noOfDoorsId: productionVehicleListTemp[i]?.doorNosId,
      upfitOptionId: productionVehicleListTemp[i]?.upfitOptionId,
      snowSetupId: productionVehicleListTemp[i]?.snowSetupId,
      snowSetup: productionVehicleListTemp[i]?.snowSetup,
      driveTrainId: productionVehicleListTemp[i]?.driveTrainId,
      vehicleType: productionVehicleListTemp[i]?.vehicleType,
      vehicleCapacity: productionVehicleListTemp[i]?.vehicleCapacity,
      noOfDoors: productionVehicleListTemp[i]?.doorNos,
      upfitOption: productionVehicleListTemp[i]?.upfitOption,
      driveTrain: productionVehicleListTemp[i]?.driveTrain,
      fuelType: productionVehicleListTemp[i]?.fuelType,
      Quantity: productionVehicleListTemp[i]?.quantity,
      Price: (productionVehicleListTemp[i]?.price !== "") ? parseFloat(getOnlyNumbers(productionVehicleListTemp[i]?.price)) : 0,
      Comments: "",
      Year: 0,
      Make: "",
      Model: "",
      Description: "",
      DriverTitleId: 0,
      DriverTitle: "",
      VinSerial: "",
      PricePerUnit: 0,
      IsUsed: true,
      poDesc1: "",
      poDesc2: "",
      assetModelId: 0,
      newAssetNo: "",
      vendorDetail: {
        addressBookNumber: 0,
        vendorName: "",
        address1: "",
        address2: "",
        address3: "",
        city: "",
        state: "",
        zip: "",
        phone: "",
        contact: "",
        contactEmail: "",
        fax: ""
      },
      reasons: [],
      vehicleDriverDetails: [],
      categoryId: 0,
      manufacturerId: 0,
      stateAvailabilityId: 0,
      programStatusId: 0,
      annualHoursId: 0,
      deckSizeId: 0,
      deckTypeId: 0,
      autonomousId: 0,
      sizeId: 0,
      typeId: 0,
      styleId: 0,
      lengthId: 0,
      fuelTankId: 0,
      chargingStationId: 0,
      treeCareEquipmentTypeId: 0,
      horsePowerId: 0,
      fuelTypeId: 0,
      vehicleFuelTypeId: productionVehicleListTemp[i]?.fuelTypeId,
      seatingCapacityId: 0,
      enclosedCabId: 0,
      capacityId: 0,
    };

    attachReasonsToRequest(orderRequest, productionVehicleListTemp[i].requestIndex, reasonCartList, true);

    apiObject?.requests.push(orderRequest);
  }

  let managedEquipmentCartListTemp = managedEquipmentCartList?.length > 0 ? [...managedEquipmentCartList] : [];
  for (let i = 0; i < managedEquipmentCartListTemp?.length; i++) {
    let orderRequest: IOrderRequest = {
      requestId: managedEquipmentCartListTemp[i]?.requestId,
      orderTypeId: managedEquipmentCartListTemp[i].orderTypeId,
      orderRequestSubTypeId: managedEquipmentCartListTemp[i]?.orderSubTypeId,
      vehicleTypeId: 0,
      vehicleCapacityId: 0,
      noOfDoorsId: 0,
      upfitOptionId: 0,
      snowSetupId: 0,
      driveTrainId: 0,
      Quantity: managedEquipmentCartListTemp[i]?.quantity,
      Price: managedEquipmentCartListTemp[i].price,
      Comments: "",
      Year: managedEquipmentCartListTemp[i]?.year,
      Make: managedEquipmentCartListTemp[i]?.make,
      Model: managedEquipmentCartListTemp[i]?.model,
      Description: managedEquipmentCartListTemp[i]?.description,
      DriverTitleId: managedEquipmentCartListTemp[i]?.driverTitleId,
      DriverTitle: managedEquipmentCartListTemp[i]?.driverTitle,
      VinSerial: "",
      PricePerUnit: 0,
      IsUsed: true,
      poDesc1: managedEquipmentCartListTemp[i]?.podesc1,
      poDesc2: managedEquipmentCartListTemp[i]?.podesc2,
      assetModelId: managedEquipmentCartListTemp[i]?.assetModelId,
      newAssetNo: "",
      vehicleType: "",
      vehicleCapacity: "",
      noOfDoors: "",
      upfitOption: "",
      snowSetup: "",
      driveTrain: "",
      fuelType: "",
      vendorDetail: {
        addressBookNumber: 0,
        vendorName: "",
        address1: "",
        address2: "",
        address3: "",
        city: "",
        state: "",
        zip: "",
        phone: "",
        contact: "",
        contactEmail: "",
        fax: ""
      },
      vehicleFuelTypeId: 0,
      reasons: [],
      vehicleDriverDetails: [],
      categoryId: managedEquipmentCartListTemp[i]?.category ? parseInt(managedEquipmentCartListTemp[i]?.category) : 0,
      manufacturerId: managedEquipmentCartListTemp[i]?.manufacturer ? parseInt(managedEquipmentCartListTemp[i]?.manufacturer): 0,
      stateAvailabilityId: managedEquipmentCartListTemp[i]?.stateAvailability ? parseInt(managedEquipmentCartListTemp[i]?.stateAvailability) : 0,
      programStatusId: managedEquipmentCartListTemp[i]?.programStatus ? parseInt(managedEquipmentCartListTemp[i]?.programStatus) : 0,
      annualHoursId: managedEquipmentCartListTemp[i]?.annualHours ? parseInt(managedEquipmentCartListTemp[i]?.annualHours) : 0,
      deckSizeId: managedEquipmentCartListTemp[i]?.deckSize ? parseInt(managedEquipmentCartListTemp[i]?.deckSize) : 0,
      deckTypeId: managedEquipmentCartListTemp[i]?.deckType ? parseInt(managedEquipmentCartListTemp[i]?.deckType) : 0,
      autonomousId: managedEquipmentCartListTemp[i]?.autonomous ? parseInt(managedEquipmentCartListTemp[i]?.autonomous) : 0,
      sizeId: managedEquipmentCartListTemp[i]?.size ? parseInt(managedEquipmentCartListTemp[i]?.size) : 0,
      typeId: managedEquipmentCartListTemp[i]?.type ? parseInt(managedEquipmentCartListTemp[i]?.type) : 0,
      styleId: managedEquipmentCartListTemp[i]?.style ? parseInt(managedEquipmentCartListTemp[i]?.style) : 0,
      lengthId: managedEquipmentCartListTemp[i]?.length ? parseInt(managedEquipmentCartListTemp[i]?.length) : 0,
      fuelTankId: managedEquipmentCartListTemp[i]?.fuelTank ? parseInt(managedEquipmentCartListTemp[i]?.fuelTank) : 0,
      chargingStationId: managedEquipmentCartListTemp[i]?.chargingStation ? parseInt(managedEquipmentCartListTemp[i]?.chargingStation) : 0,
      treeCareEquipmentTypeId: managedEquipmentCartListTemp[i]?.treeCareEquipmentType ? parseInt(managedEquipmentCartListTemp[i]?.treeCareEquipmentType) : 0,
      horsePowerId: managedEquipmentCartListTemp[i]?.horsePower ? parseInt(managedEquipmentCartListTemp[i]?.horsePower) : 0,
      fuelTypeId: managedEquipmentCartListTemp[i]?.fuelType ? parseInt(managedEquipmentCartListTemp[i]?.fuelType) : 0,
      seatingCapacityId: managedEquipmentCartListTemp[i]?.seatingCapacity ? parseInt(managedEquipmentCartListTemp[i]?.seatingCapacity) : 0,
      enclosedCabId: managedEquipmentCartListTemp[i]?.enclosedCab ? parseInt(managedEquipmentCartListTemp[i]?.enclosedCab) : 0,
      capacityId: managedEquipmentCartListTemp[i]?.capacity ? parseInt(managedEquipmentCartListTemp[i]?.capacity) : 0,
    };

    attachReasonsToRequest(orderRequest, managedEquipmentCartListTemp[i].requestIndex, reasonCartList, true);

    apiObject.requests.push(orderRequest);
  }

  let nonStandardEquipmentCartListTemp = nonStandardEquipmentCartList?.length > 0 ? [...nonStandardEquipmentCartList] : [];
  for (let i = 0; i < nonStandardEquipmentCartListTemp?.length; i++) {
    let orderRequest: IOrderRequest = {
      requestId: nonStandardEquipmentCartListTemp[i]?.requestId,
      orderTypeId: nonStandardEquipmentCartListTemp[i].orderTypeId,
      orderRequestSubTypeId: null,
      vehicleTypeId: 0,
      vehicleCapacityId: 0,
      noOfDoorsId: 0,
      upfitOptionId: 0,
      snowSetupId: 0,
      driveTrainId: 0,
      vehicleType: "",
      vehicleCapacity: "",
      noOfDoors: "",
      upfitOption: "",
      snowSetup: "",
      driveTrain: "",
      fuelType: "",
      Quantity: nonStandardEquipmentCartListTemp[i]?.quantity ?? 1,
      Price: 0,
      Comments: "",
      Year: nonStandardEquipmentCartListTemp[i]?.year,
      Make: nonStandardEquipmentCartListTemp[i]?.make,
      Model: nonStandardEquipmentCartListTemp[i]?.model,
      Description: nonStandardEquipmentCartListTemp[i]?.description,
      DriverTitle: "",
      DriverTitleId: 0,
      VinSerial: nonStandardEquipmentCartListTemp[i]?.vinSerial,
      PricePerUnit: (nonStandardEquipmentCartListTemp[i]?.pricePerUnit !== "") ? parseFloat(getOnlyNumbers(nonStandardEquipmentCartListTemp[i]?.pricePerUnit)) : 0,
      IsUsed: nonStandardEquipmentCartListTemp[i]?.isUsed,
      poDesc1: "",
      poDesc2: "",
      assetModelId: 0,
      newAssetNo: "",
      vendorDetail: {
        addressBookNumber: nonStandardEquipmentCartListTemp[i]?.abNumber,
        vendorName: nonStandardEquipmentCartListTemp[i]?.vendorName,
        address1: nonStandardEquipmentCartListTemp[i]?.address1,
        address2: nonStandardEquipmentCartListTemp[i]?.address2,
        address3: nonStandardEquipmentCartListTemp[i]?.address3,
        city: nonStandardEquipmentCartListTemp[i]?.city,
        state: nonStandardEquipmentCartListTemp[i]?.state,
        zip: nonStandardEquipmentCartListTemp[i]?.zip,
        phone: nonStandardEquipmentCartListTemp[i]?.dealerPhone,
        contact: nonStandardEquipmentCartListTemp[i]?.dealerContact,
        contactEmail: nonStandardEquipmentCartListTemp[i]?.emailAddress,
        fax: ""
      },
      reasons: [],
      vehicleDriverDetails: [],
      categoryId: 0,
      manufacturerId: 0,
      stateAvailabilityId: 0,
      programStatusId: 0,
      annualHoursId: 0,
      deckSizeId: 0,
      deckTypeId: 0,
      autonomousId: 0,
      sizeId: 0,
      typeId: 0,
      styleId: 0,
      lengthId: 0,
      fuelTankId: 0,
      chargingStationId: 0,
      treeCareEquipmentTypeId: 0,
      horsePowerId: 0,
      fuelTypeId: 0,
      seatingCapacityId: 0,
      enclosedCabId: 0,
      capacityId: 0,
      vehicleFuelTypeId: 0
    };

    attachReasonsToRequest(orderRequest, nonStandardEquipmentCartListTemp[i].requestIndex, reasonCartList, false);

    apiObject.requests.push(orderRequest);
  }

  let managementVehicleCartListTemp = managementVehicleCartList?.length > 0 ? [...managementVehicleCartList] : [];
  for (let i = 0; i < managementVehicleCartListTemp?.length; i++) {
    let orderRequest: IOrderRequest = {
      requestId: managementVehicleCartListTemp[i]?.requestId,
      orderTypeId: managementVehicleCartListTemp[i].orderTypeId,
      orderRequestSubTypeId: managementVehicleCartListTemp[i].orderSubTypeId,
      vehicleTypeId: managementVehicleCartListTemp[i]?.vehicleTypeId,
      vehicleCapacityId: 0,
      noOfDoorsId: 0,
      upfitOptionId: 0,
      snowSetupId: 0,
      vehicleType: managementVehicleCartListTemp[i]?.vehicleType,
      vehicleCapacity: "",
      noOfDoors: "",
      upfitOption: "",
      snowSetup: "",
      fuelType: managementVehicleCartListTemp[i]?.fuelType,
      driveTrain: managementVehicleCartListTemp[i]?.driveTrain,
      driveTrainId: managementVehicleCartListTemp[i]?.driveTrainId,
      Quantity: managementVehicleCartListTemp[i]?.quantity,
      Price: managementVehicleCartListTemp[i]?.price,
      Comments: "",
      Year: 0,
      Make: "",
      Model: "",
      Description: "",
      DriverTitleId: managementVehicleCartListTemp[i]?.driverTitleId,
      DriverTitle: managementVehicleCartListTemp[i]?.driverTitle,
      VinSerial: "",
      PricePerUnit: 0,
      IsUsed: true,
      poDesc1: "",
      poDesc2: "",
      assetModelId: 0,
      newAssetNo: "",
      vendorDetail: {
        addressBookNumber: 0,
        vendorName: "",
        address1: "",
        address2: "",
        address3: "",
        city: "",
        state: "",
        zip: "",
        phone: "",
        contact: "",
        contactEmail: "",
        fax: ""
      },
      reasons: [],
      vehicleDriverDetails: managementVehicleCartListTemp[i].vehicleDriverDetails,
      categoryId: 0,
      manufacturerId: 0,
      stateAvailabilityId: 0,
      programStatusId: 0,
      annualHoursId: 0,
      deckSizeId: 0,
      deckTypeId: 0,
      autonomousId: 0,
      sizeId: 0,
      typeId: 0,
      styleId: 0,
      lengthId: 0,
      fuelTankId: 0,
      chargingStationId: 0,
      treeCareEquipmentTypeId: 0,
      horsePowerId: 0,
      fuelTypeId: 0,
      vehicleFuelTypeId: managementVehicleCartListTemp[i]?.fuelTypeId,
      seatingCapacityId: 0,
      enclosedCabId: 0,
      capacityId: 0,
    };

    attachReasonsToRequest(orderRequest, managementVehicleCartListTemp[i].requestIndex, reasonCartList, true);

    apiObject.requests.push(orderRequest);
  }
  dispatch(RequisitionAction.setIOrderRequest(apiObject));
}

export const APIToReact = (props: any) => {
  // Declarations
  const apiObject = props.orderByIdValue;
  const dispatch = props.dispatch;

  const productionVehicleListTemp = [];
  const nonStandardEquipmentListTemp = [];
  const managedEquipmentListTemp = [];
  const managementVehicleListTemp = [];
  const reasonListTemp: any = [];
  const requestList = [];
  let requestIndex = 0;
  let requestType = "";
  let productionVehicleIndex = 0;
  let nonStandardEquipmentIndex = 0;
  let managedEquipmentIndex = 0;
  let managementVehicleIndex = 0;
  let reasonIndex = 0;

  let requestListExist = apiObject?.requests?.length > 0 ? [...apiObject.requests] : [];

  for (let i = 0; i < requestListExist?.length; i++) {
    let reasonList = requestListExist[i].reasons?.length > 0 ? [...requestListExist[i].reasons] : [];
    //Request
    requestType = "";
    if (requestListExist[i].orderType == "Vehicle Request" && requestListExist[i].subOrderType == "Production Vehicle") {
      requestType = CONSTANTS.PRODUCTION_VEHICLE;
    } else if (requestListExist[i].orderType == "Non-Standard Equipment") {
      requestType = CONSTANTS.NON_STANDARD_EQUIPMENT;
    } else if (requestListExist[i].orderType == CONSTANTS.MANAGED_EQUIPMENT) {
      requestType = CONSTANTS.MANAGED_EQUIPMENT;
    } else if (requestListExist[i].orderType == "Vehicle Request" && requestListExist[i].subOrderType == "Management Vehicle") {
      requestType = CONSTANTS.MANAGEMENT_VEHICLE;
    }

    requestIndex = i + 1;
    const request: IRequest = {
      requestIndex: requestIndex,
      requestType: requestType,
      showQuotePrice: requestListExist[i].isManagedEquipmentDealerQuotePriceExists
    }
    requestList.push(request);

    //production vehicle
    if (requestType == CONSTANTS.PRODUCTION_VEHICLE) {

      productionVehicleIndex = productionVehicleIndex + 1;

      const productionVehicle: IProductionVehicle = {
        requestIndex: request?.requestIndex,
        requestType: request?.requestType,
        requestId: requestListExist[i].requestId,
        orderTypeId: requestListExist[i].orderTypeId.toString(),
        orderSubTypeId: (requestListExist[i].orderRequestSubTypeId != null && requestListExist[i].orderRequestSubTypeId != undefined) ? requestListExist[i].orderRequestSubTypeId.toString() : "",
        productionVehicleIndex: productionVehicleIndex,
        vehicleType: requestListExist[i].vehicleType,
        vehicleCapacity: requestListExist[i].vehicleCapacity,
        doorNos: requestListExist[i].noOfDoors,
        price: requestListExist[i]?.unitPrice?.toString(),
        quantity: requestListExist[i].quantity,
        vehicleTypeId: requestListExist[i].requestSearchCriteria?.vehicleTypeId,
        showVehicleCapacity: requestListExist[i].requestSearchCriteria?.showVehicleCapacity,
        vehicleCapacityId: requestListExist[i].requestSearchCriteria?.vehicleCapacityId,
        doorNosId: requestListExist[i].requestSearchCriteria?.noOfDoorsId,
        upfitOption: requestListExist[i].upfitOption,
        driveTrain: requestListExist[i].driveTrain,
        upfitOptionId: requestListExist[i].requestSearchCriteria?.upfitOptionId,
        driveTrainId: requestListExist[i].requestSearchCriteria?.driveTrainId,
        snowSetup: requestListExist[i].snowSetup,
        snowSetupId: requestListExist[i].requestSearchCriteria?.snowSetupId,
        isExistInCart: true,
        fuelType: requestListExist[i]?.fuelType,
        fuelTypeId: requestListExist[i]?.requestSearchCriteria?.vehicleFuelTypeId?.toString()
      }

      productionVehicleListTemp.push(productionVehicle);
    } else if (requestType == CONSTANTS.NON_STANDARD_EQUIPMENT) {
      //nonStandardEquipment
      nonStandardEquipmentIndex = nonStandardEquipmentIndex + 1;

      const nonStandardEquipment: INonStandardEquipment = {
        requestIndex: request?.requestIndex,
        requestType: request?.requestType,
        requestId: requestListExist[i].requestId,
        nonStandardEquipmentIndex: nonStandardEquipmentIndex,
        orderTypeId: requestListExist[i].orderTypeId.toString(),
        make: requestListExist[i].make,
        model: requestListExist[i].model,
        year: requestListExist[i].year,
        isUsed: requestListExist[i].isUsed,
        vinSerial: requestListExist[i].vinSerial,
        pricePerUnit: requestListExist[i].pricePerUnit,
        description: requestListExist[i].description,
          quantity: requestListExist[i].quantity ?? 1,
        abNumber: requestListExist[i]?.vendorDetail?.addressBookNumber,
        vendorName: requestListExist[i]?.vendorDetail?.vendorName,
        dealerContact: requestListExist[i]?.vendorDetail?.contact,
        dealerPhone: requestListExist[i]?.vendorDetail?.phone,
        emailAddress: requestListExist[i]?.vendorDetail?.contactEmail,
        address1: requestListExist[i]?.vendorDetail?.address1,
        address2: requestListExist[i]?.vendorDetail?.address2,
        address3: requestListExist[i]?.vendorDetail?.address3,
        city: requestListExist[i]?.vendorDetail?.city,
        state: requestListExist[i]?.vendorDetail?.state,
        zip: requestListExist[i]?.vendorDetail?.zip,
        isExistInCart: true,
      }

      nonStandardEquipmentListTemp.push(nonStandardEquipment);
    } else if (requestType == CONSTANTS.MANAGED_EQUIPMENT) {

      managedEquipmentIndex = managedEquipmentIndex + 1;

      const managedEquipment: IManagedEquipment = {
        requestIndex: request?.requestIndex,
        requestType: request?.requestType,
        requestId: requestListExist[i].requestId,
        price: requestListExist[i].unitPrice,
        orderTypeId: requestListExist[i].orderTypeId,
        orderSubTypeId: (requestListExist[i].orderRequestSubTypeId != null && requestListExist[i].orderRequestSubTypeId != undefined) ? requestListExist[i].orderRequestSubTypeId.toString() : "",
        managedEquipmentIndex: 0,
        equipmentType: requestListExist[i].subOrderType,
        stateAvailability: requestListExist[i].requestSearchCriteria?.stateAvailabilityId?.toString(),
        manufacturer: requestListExist[i].requestSearchCriteria?.manufacturerId?.toString(),
        programStatus: requestListExist[i].requestSearchCriteria?.programStatusId?.toString(),
        annualHours: requestListExist[i].requestSearchCriteria?.annualHoursId?.toString(),
        category: requestListExist[i].requestSearchCriteria?.categoryId?.toString(),
        deckSize: requestListExist[i].requestSearchCriteria?.deckSizeId?.toString(),
        deckType: requestListExist[i].requestSearchCriteria?.deckTypeId?.toString(),
        autonomous: requestListExist[i].requestSearchCriteria?.autonomousId?.toString(),
        seatingCapacity: requestListExist[i].requestSearchCriteria?.seatingCapacityId?.toString(),
        enclosedCab: requestListExist[i].requestSearchCriteria?.enclosedCabId?.toString(),
        capacity: requestListExist[i].requestSearchCriteria?.capacityId?.toString(),
        style: requestListExist[i].requestSearchCriteria?.styleId?.toString(),
        length: requestListExist[i].requestSearchCriteria?.lengthId?.toString(),
        fuelTank: requestListExist[i].requestSearchCriteria?.fuelTankId?.toString(),
        chargingStation: requestListExist[i].requestSearchCriteria?.chargingStationId?.toString(),
        size: requestListExist[i].requestSearchCriteria?.sizeId?.toString(),
        type: requestListExist[i].requestSearchCriteria?.typeId?.toString(),
        treeCareEquipmentType: requestListExist[i]?.requestSearchCriteria?.treeCareEquipmentTypeId?.toString(),
        horsePower: requestListExist[i].requestSearchCriteria?.horsePowerId?.toString(),
        fuelType: requestListExist[i]?.requestSearchCriteria?.fuelTypeId?.toString(),
        quantity: requestListExist[i]?.quantity,
        isExistInCart: true,

        assetModelId: requestListExist[i]?.assetModelId,
        make: requestListExist[i]?.make,
        model: requestListExist[i]?.model,
        year: requestListExist[i]?.year,
        monthlyExpense: requestListExist[i]?.monthlyExpense,
        description: requestListExist[i]?.description,
        podesc1: requestListExist[i]?.podesc1,
        podesc2: requestListExist[i]?.podesc2,
        design: "",
        driverTitle: requestListExist[i]?.driverTitle,
        showQuotePrice: requestListExist[i]?.isManagedEquipmentDealerQuotePriceExists
      }

      managedEquipmentListTemp.push(managedEquipment);
    } else if (requestType == CONSTANTS.MANAGEMENT_VEHICLE) {

      managementVehicleIndex = managementVehicleIndex + 1;

      let driverDetails = [...requestListExist[i].vehicleDriverDetails];
      let vehicleDriverDetailsTemp = [];
      for (let i = 0; i < driverDetails?.length; i++) {
        const driverDetail: IVehicleDriverDetails = {
          orderRequestVehicleDriverId: driverDetails[i].orderRequestVehicleDriverId,
          employeeNumber: driverDetails[i].employeeNumber,
          employeeFullName: driverDetails[i].employeeName
        }
        vehicleDriverDetailsTemp.push(driverDetail);
      }

      const managementVehicle: IManagementVehicle = {
        requestIndex: request?.requestIndex,
        requestType: request?.requestType,
        requestId: requestListExist[i]?.requestId,
        orderTypeId: requestListExist[i]?.orderTypeId.toString(),
        orderSubTypeId: requestListExist[i]?.orderRequestSubTypeId.toString(),
        managementVehicleIndex: managementVehicleIndex,
        driverTitle: requestListExist[i]?.driverTitle,
        driverTitleId: requestListExist[i]?.driverTitleId,
        price: requestListExist[i]?.unitPrice,
        quantity: requestListExist[i]?.quantity,
        isExistInCart: true,
        vehicleDriverDetails: vehicleDriverDetailsTemp,
        vehicleDriverDetailsError: requestListExist[i]?.vehicleDriverDetailsError,
        driveTrain: requestListExist[i]?.driveTrain,
        vehicleType: requestListExist[i]?.vehicleType,
        driveTrainId: requestListExist[i]?.requestSearchCriteria?.driveTrainId,
        vehicleTypeId: requestListExist[i]?.requestSearchCriteria?.vehicleTypeId,
        showVehicleCapacity: requestListExist[i].requestSearchCriteria?.showVehicleCapacity,
        fuelType: requestListExist[i]?.fuelType,
        fuelTypeId: requestListExist[i]?.requestSearchCriteria?.vehicleFuelTypeId?.toString(),

      }

      managementVehicleListTemp.push(managementVehicle);
    }

    // Reasons
    for (let j = 0; j < reasonList?.length; j++) {
      reasonIndex = reasonIndex + 1;

      const reason: IReason = initializeReason(requestList, 0);

      reason.requestIndex = request?.requestIndex;
      reason.requestType = request?.requestType;
      reason.reasonIndex = reasonIndex;
      reason.reasonId = reasonList[j].reasonId;
      reason.reasonTypeId = reasonList[j].reasonTypeId;
      reason.reasonType = reasonList[j].reasonType;
      reason.shipToLocation = reasonList[j].shipToLocation;
      reason.newEmployeeBusiness = (reasonList[j].isNewEmployee) ? CONSTANTS.NEW_EMPLOYEE : CONSTANTS.NEW_BUSINESS;
      reason.jobIdOrEmpId = (reasonList[j].jobOrEmpId != null) ? reasonList[j].jobOrEmpId : "";
      reason.jobOrEmpName = (reasonList[j].jobOrEmpName != null) ? reasonList[j].jobOrEmpName : "";
      reason.salesAmount = (reasonList[j].salesAmount != null && reasonList[j].salesAmount != 0) ? formatPrice(reasonList[j].salesAmount, 2) : reasonList[j].salesAmount == "0.00" ? "0.00" : "";
      reason.asset = (reasonList[j].assetNumber != null && reasonList[j].assetNumber != 0) ? reasonList[j].assetNumber?.toString() : "";
      reason.unitNumber = (reasonList[j].equipmentNumber != null) ? reasonList[j].equipmentNumber : "";
      reason.netBookedValue = (reasonList[j].netBookedValue != null && reasonList[j].netBookedValue != 0) ? reasonList[j].netBookedValue?.toString() : "";
      reason.mileageHourMeter = (reasonList[j].mileageHourMeter != null && reasonList[j].mileageHourMeter != 0) ? formatPrice(reasonList[j].mileageHourMeter, 1) : "";
      reason.dispositionTypeId = (reasonList[j].dispositionTypeId != null) ? reasonList[j].dispositionTypeId : 0;
      reason.dispositionType = (reasonList[j].dispositionType != null) ? reasonList[j].dispositionType : "";
      reason.statusId = (reasonList[j].dispositionTypeId != null) ? reasonList[j].dispositionTypeId : 0;
      reason.status = (reasonList[j].dispositionType != null) ? reasonList[j].dispositionType : "";
      reason.conditionId = (reasonList[j].conditionTypeId != null) ? reasonList[j].conditionTypeId : 0;
      reason.condition = (reasonList[j].conditionType != null) ? reasonList[j].conditionType : "";
      reason.description = (reasonList[j].description != null) ? reasonList[j].description : "";
      reason.modelYear = (reasonList[j].year != null) ? reasonList[j].year.toString() : "";
      const replacementAssetDisposalDate = reasonList[j].replacementAssetDisposalDate ?? "";
      reason.replacementAssetDisposalDate = replacementAssetDisposalDate ? moment(replacementAssetDisposalDate).format("MM/DD/YYYY") : "";
      reason.comments = (reasonList[j].comments != null) ? reasonList[j].comments : "";
      reason.anticipatedDisposalPrice = (reasonList[j].approximateDisposeValue != null) ? formatPrice(reasonList[j].approximateDisposeValue, 2) : null;
      reason.reason = (reasonList[j].reason != null) ? reasonList[j].reason : "";
      reason.attachments = reasonList[j].reasonAttachments;
      reason.isValidAsset = false;
      reason.hasQuoteAttached = reasonList[j].hasQuoteAttached
      
      reason.showQuotePrice = (request?.requestType == CONSTANTS.MANAGED_EQUIPMENT ?  requestListExist[i].isManagedEquipmentDealerQuotePriceExists : (requestListExist[i].isManagedEquipmentDealerQuotePriceExists && reasonList[j]?.managedEquipmentDealerQuotePriceVM?.quotePrice != null) ? requestListExist[i].isManagedEquipmentDealerQuotePriceExists : false );
      reason.quotePrice = (reasonList[j]?.managedEquipmentDealerQuotePriceVM?.quotePrice != null) ? formatPrice(reasonList[j]?.managedEquipmentDealerQuotePriceVM?.quotePrice, 2) : "";
      reason.estimatedPrice = formatPrice(reasonList[j]?.managedEquipmentDealerQuotePriceVM?.estimatedPrice, 2);
      reason.name = reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerName;
      reason.contact = reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerContact;
      reason.phone = reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerPhone != null ? reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerPhone : "";
      reason.email = reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerEmail;
      reason.addressLine1 = reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerAdd1;
      reason.addressLine2 = reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerAdd2;
      reason.addressLine3 = reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerAdd3;
      reason.city = reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerCity;
      reason.state = reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerState;
      reason.zipCode = reasonList[j].managedEquipmentDealerQuotePriceVM?.dealerZip;
      reason.showNewEmployee = reasonList[j].jobOrEmpId ? false : true



      if (reason.asset?.length > 0 && reason.description?.length > 0) {
        reason.assetError = "";
        reason.isValidAsset = true;
      }
      let responseReason = RequisitionValidation.ValidateReason({ ...reason });
      reasonListTemp.push(responseReason);
    }
    
  }
  const managedEquipmentReasons = reasonListTemp?.filter(
    (r: any) => r?.requestType === CONSTANTS.MANAGED_EQUIPMENT
  );

  const firstManagedEquipmentReason = managedEquipmentReasons[0];

   reasonListTemp?.map((reasonItem: any, index: any) => {
    const shouldDisable = apiObject?.isDealerSame &&
    reasonListTemp?.length > 1 &&
    reasonListTemp?.some(
        (r: any, i: any) =>
          i !== index && r?.requestType === CONSTANTS.MANAGED_EQUIPMENT
      );

    if (apiObject?.isDealerSame && reasonItem?.requestType === CONSTANTS.MANAGED_EQUIPMENT) {
      
      reasonItem.isDisabled= shouldDisable && index !== reasonListTemp.indexOf(firstManagedEquipmentReason)
     
    }
  });
  const orderAttachmentTemp = [];
  let orderAttachment = apiObject?.attachmentFileObjList?.length > 0 ? [...apiObject.attachmentFileObjList] : [];

  for (let i = 0; i < orderAttachment?.length; i++) {
    const attachment: IAttachment = {
      fileName: orderAttachment[i].name,
      mimeType: orderAttachment[i].type,
      attachmentUrl: orderAttachment[i].path
    }
    orderAttachmentTemp.push(attachment);
  }
  let branchValue = { value: apiObject?.costCenter, label: apiObject?.costCenter, text: apiObject?.branchId, branchName: apiObject?.branchName}
  dispatch(RequisitionAction.setOrderId(apiObject?.orderId));
  dispatch(RequisitionAction.setSelectedBranch(branchValue));
  dispatch(RequisitionAction.setIRequest(requestList));
  dispatch(RequisitionAction.setIProductionVehicle(productionVehicleListTemp));
  dispatch(RequisitionAction.setINonStandardEquipment(nonStandardEquipmentListTemp));
  dispatch(RequisitionAction.setIManagedEquipment(managedEquipmentListTemp));
  dispatch(RequisitionAction.setIManagementVehicle(managementVehicleListTemp));
  dispatch(RequisitionAction.setIReason(reasonListTemp));

  let requestCartList = [];
  for (let i = 0; i < requestList?.length; i++) {
    requestCartList.push({ ...requestList[i] })
  }

  let productionVehicleCartListTemp = [];
  for (let i = 0; i < productionVehicleListTemp?.length; i++) {
    productionVehicleCartListTemp.push({ ...productionVehicleListTemp[i] })
  }


  let managedEquipmentCartListTemp = [];
  for (let i = 0; i < managedEquipmentListTemp?.length; i++) {
    managedEquipmentCartListTemp.push({ ...managedEquipmentListTemp[i] })
  }

  let managementVehicleCartListTemp = [];
  for (let i = 0; i < managementVehicleListTemp?.length; i++) {
    managementVehicleCartListTemp.push({ ...managementVehicleListTemp[i] })
  }

  let reasonCartListTemp = [];
  for (let i = 0; i < reasonListTemp?.length; i++) {
    reasonCartListTemp.push({ ...reasonListTemp[i] })
  }

  dispatch(RequisitionAction.setCartIRequest(requestCartList));
  dispatch(RequisitionAction.setCartIProductionVehicle(productionVehicleCartListTemp));
  dispatch(RequisitionAction.setCartINonStandardEquipment(nonStandardEquipmentListTemp));
  dispatch(RequisitionAction.setCartIManagedEquipment(managedEquipmentCartListTemp));
  dispatch(RequisitionAction.setCartIManagementVehicle(managementVehicleCartListTemp));
  dispatch(RequisitionAction.setCartIReason(reasonCartListTemp));

  dispatch(RequisitionAction.setShowItemDetails(true));
  UploadAttachmentsDispatch.dispatchSetAttachments(dispatch)(orderAttachmentTemp);
}
